import cloneDeep from 'clone-deep'
import type { IContractorOriginSection } from 'config'
import type { InputType } from 'config/input.type.constants'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { updateContractorApplication } from 'services/apis'
import { FormTableHeader, Tooltip } from 'stories/components'
import { InputConvert } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import {
  getContractorFieldValues,
  getVisibleContractorInputs,
  isContractorInputVisible,
} from '../contractorInputVisibility'
import { validateInputRecord } from '../validateInputRecord'

type GeneralInformationProps = {
  inputs: Record<string, InputType>
  capabilityInputs?: Record<string, InputType>
  sections?: IContractorOriginSection[]
  setInputs: (v: Record<string, InputType> | ((prev: Record<string, InputType>) => Record<string, InputType>)) => void
  partyContractorInfo: Record<string, unknown>
  onStatusChange: (ok: boolean) => void
  canEdit: boolean
  contractorURL: any
  showHistory: (key: string, headers?: Array<FormTableHeader>) => void
  setLoading: (v: boolean) => void
}

export function GeneralInformation({
  inputs,
  capabilityInputs = {},
  sections = [],
  setInputs,
  partyContractorInfo,
  onStatusChange,
  canEdit,
  contractorURL,
  showHistory,
  setLoading,
}: GeneralInformationProps) {
  const { no: noParam } = useParams() as { no: string }
  const no = Number(noParam)
  const [fieldValueChanged, setFieldValueChanged] = useState(false)

  const contractorFieldValues = useMemo(
    () => getContractorFieldValues(inputs, capabilityInputs),
    [inputs, capabilityInputs],
  )

  const onChangeGeneralInfo = (key: string, value: string) => {
    setFieldValueChanged(true)
    const newInputs = cloneDeep(inputs)
    newInputs[key].value = InputConvert(newInputs[key], value)
    const fieldValues = getContractorFieldValues(newInputs, capabilityInputs)
    const { data, success } = validateInputRecord(getVisibleContractorInputs(newInputs, fieldValues))
    setInputs({ ...newInputs, ...data })
    onStatusChange(success)
  }

  const onBlurGeneralInfo = async (key: string) => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    const input: any = inputs[key]
    if (input.error && input.error.length > 0) return
    setLoading(true)
    await updateContractorApplication(no, { [key]: input.value } as any)
    setLoading(false)
  }

  const fillPartyContractorInfo = async () => {
    const newInputs = cloneDeep(inputs)
    Object.keys(partyContractorInfo).forEach((k) => {
      newInputs[k].value = InputConvert(newInputs[k], partyContractorInfo[k])
    })
    setLoading(true)
    await updateContractorApplication(no, partyContractorInfo)
    setLoading(false)
    const fieldValues = getContractorFieldValues(newInputs, capabilityInputs)
    const { data, success } = validateInputRecord(getVisibleContractorInputs(newInputs, fieldValues))
    setInputs({ ...newInputs, ...data })
    onStatusChange(success)
  }

  const renderField = (key: string) => {
    let input = inputs[key]
    if (!input || !isContractorInputVisible(input, contractorFieldValues)) return null

    if (key === 'entityTitle' && !contractorURL && canEdit && Object.keys(partyContractorInfo).length) {
      let tooltip = ''
      Object.keys(partyContractorInfo).forEach((k) => {
        tooltip += `<div>${inputs[k].title}: ${partyContractorInfo[k]}</div>`
      })
      input = {
        ...input,
        additionalElements: (
          <span className={`text-shade-blue hover:underline cursor-pointer`}>
            <Tooltip message={`<div>${tooltip}</div>`}>
              <span className="font-variation-settings-600 px-3" onClick={fillPartyContractorInfo}>
                Fill Party Contractor
              </span>
            </Tooltip>
          </span>
        ),
      }
    }
    input = { ...input, disabled: !canEdit }
    return (
      <div className="input md:col-span-1" key={key}>
        <RenderInput
          input={input}
          Key={key}
          onChange={onChangeGeneralInfo}
          showHistory={showHistory}
          onBlur={onBlurGeneralInfo}
        />
      </div>
    )
  }

  const sectionFieldKeys = useMemo(() => {
    const keys = new Set<string>()
    sections.forEach((s) => s.fields.forEach((k) => keys.add(k)))
    return keys
  }, [sections])

  const freeKeys = Object.keys(inputs).filter((key) => !sectionFieldKeys.has(key))
  const showHeaders = sections.filter((s) => s.fields.some((k) => inputs[k])).length > 1

  return (
    <div className="mb-3">
      {sections.map((section) => {
        const visibleFields = section.fields.filter((key) => inputs[key])
        if (!visibleFields.length) return null
        return (
          <div key={section.id} className="mb-8">
            {(showHeaders || section.title) && section.title && (
              <div className="mb-6 pb-1">
                <RenderInput
                  Key={`section-${section.id}`}
                  input={{ inputType: 'section', title: section.title }}
                  onChange={() => {}}
                />
              </div>
            )}
            <div className="grid gap-4 md:grid-cols-2 grid-cols-1">{visibleFields.map(renderField)}</div>
          </div>
        )
      })}
      {!!freeKeys.length && (
        <div className="grid gap-4 md:grid-cols-2 grid-cols-1">{freeKeys.map(renderField)}</div>
      )}
    </div>
  )
}
